import { useState, memo, useMemo } from 'react';
import './VideoTimeline.css';

const VideoTimeline = memo(({ 
  currentTime = 0, 
  duration = 0, 
  events = [],
  onSeek,
  onMarkerClick,
  className = ''
}) => {
  const [hoverTime, setHoverTime] = useState(null);

  // Memoize the progress percentage to avoid recalculation on every render
  const progressPercentage = useMemo(() => {
    return duration > 0 ? (currentTime / duration) * 100 : 0;
  }, [currentTime, duration]);

  const handleClick = (e) => {
    if (!duration || !onSeek) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const time = percentage * duration;
    onSeek(time);
  };

  const handleMouseMove = (e) => {
    if (!duration) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const time = percentage * duration;
    setHoverTime(time);
  };

  const handleMouseLeave = () => {
    setHoverTime(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const actionEvents = events.filter(e => e.type === 'action' || e.videoOffsetMs !== undefined);

  return (
    <div 
      className={`zibby-video-timeline ${className}`}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: 'pointer' }}
    >
      <div 
        className="zibby-video-progress-fill" 
        style={{ width: `${progressPercentage}%` }}
      />
      
      {actionEvents.map((event, i) => {
        const percentage = (event.videoOffsetMs / 1000 / duration) * 100;
        const isEvidence = event.isEvidence;
        const verdict = event.verdict;
        const markerClass = isEvidence ? `zibby-timeline-marker zibby-timeline-evidence zibby-timeline-${verdict}` : 'zibby-timeline-marker';
        const title = isEvidence 
          ? `📸 Evidence: ${event.description || (verdict === 'pass' ? 'Test Passed' : 'Test Failed')}`
          : `${event.data?.tool || event.tool || 'Event'}`;
        
        return (
          <div
            key={i}
            className={markerClass}
            style={{ left: `${percentage}%` }}
            title={title}
            onClick={(e) => {
              e.stopPropagation();
              if (onMarkerClick) {
                onMarkerClick(event.videoOffsetMs / 1000);
              }
            }}
          >
            {isEvidence && <span className="zibby-evidence-icon">{verdict === 'pass' ? '✓' : '✗'}</span>}
          </div>
        );
      })}

      {hoverTime !== null && (
        <div 
          className="zibby-timeline-tooltip" 
          style={{ left: `${(hoverTime / duration) * 100}%` }}
        >
          {formatTime(hoverTime)}
        </div>
      )}
    </div>
  );
});

VideoTimeline.displayName = 'VideoTimeline';

export default VideoTimeline;

